import { signupReferer } from '@gonfalon/constants/constants';
import { enableSignupRefererForwarding } from '@gonfalon/dogfood-flags/dogfood-flags';

/** Match accounts.SignupSource* in internal/accounts/campaign.go. */
export const SIGNUP_SOURCE_AGENT_CONTROL = 'agent-control';
export const SIGNUP_SOURCE_AWS_MARKETPLACE = 'aws-marketplace';

export const AGENT_SOURCE_VALUE = 'agent';
export const LD_SITE_SOURCE_VALUE = 'ldwebsite';

const AGENT_UTM_PARAMS = {
  utm_source: 'agent',
  utm_medium: 'product',
  utm_campaign: 'agent_onb',
} as const;

const LD_SITE_UTM_PARAMS = {
  utm_source: 'website',
  utm_medium: 'web',
  utm_campaign: 'agent_onb',
} as const;

/**
 * Builds a customReferer URL string for the signup POST body. When signup came
 * from the agent (source=agent) or the LD website (source=ldwebsite), the UTMs
 * are baked into the referer so they flow through the existing referer pipeline
 * without mutating the browser URL.
 */
export function buildSignupCustomReferer(searchParams: URLSearchParams): string {
  const base = window.location.href;

  const source = searchParams.get('source');
  let utmParams;
  if (source === AGENT_SOURCE_VALUE) {
    utmParams = AGENT_UTM_PARAMS;
  } else if (source === LD_SITE_SOURCE_VALUE) {
    utmParams = LD_SITE_UTM_PARAMS;
  } else {
    return base;
  }

  const url = new URL(base);
  for (const [key, value] of Object.entries(utmParams)) {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

export function getForwardedReferer(
  searchParams: URLSearchParams = new URLSearchParams(window.location.search),
): string {
  const existingReferer = searchParams.get('referer');
  return existingReferer ?? buildSignupCustomReferer(searchParams);
}

/**
 * Paths whose own URL may be used as an attribution source.
 *
 * Only static routes qualify. The other unauthenticated routes interpolate user
 * data — `/choose-organization/:email`, `/welcome/:token`, `/signup/:token`,
 * `/reset/:passwordResetToken` — and using `window.location.href` there would
 * copy it into the `referer` param and onward into Segment.
 *
 * Mirrored by `captureSignupRefererUnauth` (internal/site/site.go); keep in sync.
 *
 * `captureCrossSiteSignupRefererUnauth` is not mirrored here, and must not be: the routes
 * it wraps are reached by a third party's cross-site POST, so `window.location.href` is a
 * URL the visitor never chose to navigate to. Those pages forward the backend's echoed
 * capture instead, which is what preserves an earlier campaign when the POST carries none.
 */
export const ATTRIBUTION_SOURCE_PATHS = new Set(['/signup', '/signup-agentcontrol', '/signup-aws', '/login', '/join']);

/**
 * Signup source implied by the entry point, keyed on the exact normalized path
 * so a longer route sharing a prefix does not inherit the wrong source.
 *
 * Deliberately wider than ATTRIBUTION_SOURCE_PATHS: the AWS fulfillment route
 * declares its source, which the trial length depends on, without becoming a
 * frontend attribution source. Its UTMs are captured server-side instead, by
 * `captureCrossSiteSignupRefererUnauth`, and reach this page as the echoed cookie.
 */
const SIGNUP_SOURCE_BY_PATH = new Map([
  ['/signup-agentcontrol', SIGNUP_SOURCE_AGENT_CONTROL],
  ['/signup-aws', SIGNUP_SOURCE_AWS_MARKETPLACE],
  ['/signup-aws/fulfillment', SIGNUP_SOURCE_AWS_MARKETPLACE],
]);

/** The signup source an entry-point path implies, or undefined when it implies none. */
export function inferSignupSourceFromPath(pathname: string): string | undefined {
  return SIGNUP_SOURCE_BY_PATH.get(normalizePath(pathname));
}

function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

function pageIsAttributionSource(pathname: string): boolean {
  return ATTRIBUTION_SOURCE_PATHS.has(normalizePath(pathname));
}

/**
 * The best attribution this page can offer, as a referer URL.
 *
 * Decorates the next hop, and is the signupv2 POST's `customReferer` fallback.
 * signupv2 will not backfill from the cookie itself, so a page with no landing
 * URL stashed has to supply one or the signup is persisted unattributed.
 */
export function resolveAttributionReferer(): string {
  if (pageIsAttributionSource(window.location.pathname)) {
    return enableSignupRefererForwarding() ? getForwardedReferer() : window.location.href;
  }
  // Anywhere else forwards only a referer it was explicitly given, never its own URL.
  if (!enableSignupRefererForwarding()) {
    return '';
  }
  // `signupReferer` is the backend's echo of the captured cookie, and is what covers the
  // OAuth callback: Google POSTs there cross-site with no query, so the page has nothing
  // of its own and the cookie is HttpOnly. Last, not first — an explicit param is this
  // hop's own attribution, matching ResolveSignupReferer.
  const explicit = new URLSearchParams(window.location.search).get('referer');
  return explicit ?? signupReferer() ?? '';
}

/** Mirrors the backend's clickIDKeys (internal/signup_attribution/attribution.go); no shared prefix. */
const CLICK_ID_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'yclid',
  'li_fat_id',
  'fbclid',
  'twclid',
  'igshid',
  'ttclid',
  'awc',
  'irclickid',
  'cjevent',
] as const;

/**
 * Pulls the attribution params out of a resolved referer — from the referer
 * rather than `window.location`, because a forwarded `?referer=` landing URL is
 * where the UTMs live.
 *
 * Every `utm_` param is carried, so `utm_id` and the rest of the GA4 set need no
 * change here. The backend still allowlists what it *stores* (utmSlugKeys +
 * clickIDKeys); anything else is client-side measurement only. That includes
 * utm_entry_page and utm_referrer, which hold full URLs and are capped at 2048
 * bytes each server-side — if signup links get unwieldy, they are the first
 * thing to drop.
 */
function attributionParamsFromReferer(referer: string): Record<string, string> {
  try {
    return attributionParams(new URL(referer, window.location.origin).searchParams);
  } catch {
    return {};
  }
}

/** The attribution params present in a query, by the rules described above. */
function attributionParams(query: URLSearchParams): Record<string, string> {
  const params: Record<string, string> = {};
  const keys = new Set<string>(CLICK_ID_PARAMS);
  for (const key of query.keys()) {
    if (key.startsWith('utm_')) {
      keys.add(key);
    }
  }

  for (const key of keys) {
    // `get`, not the iterated value: a repeated key keeps its first value.
    const value = query.get(key);
    if (value) {
      params[key] = value;
    }
  }
  return params;
}

/**
 * Search params to carry from signup to the email verification page.
 *
 * `referer` stays the authoritative carrier for the backend. The attribution
 * params are lifted out alongside it so client-side analytics can slice the
 * signup -> verification step by campaign — invisible inside an encoded referer
 * — and so a navigation that drops `referer` doesn't take everything with it.
 */
export function getSignupAttributionSearchParams(): Record<string, string> {
  const search: Record<string, string> = {};
  const referer = resolveAttributionReferer();

  if (referer) {
    search.referer = referer;
    Object.assign(search, attributionParamsFromReferer(referer));
  }

  const signupSource = SIGNUP_SOURCE_BY_PATH.get(normalizePath(window.location.pathname));
  if (signupSource) {
    search.signup_source = signupSource;
  }

  return search;
}

/**
 * The attribution params the current page carries, for forwarding to the next
 * one.
 *
 * Every hop after signup rebuilds its own link and forwards only the keys it
 * knows to look for, so without this the params lifted by
 * `getSignupAttributionSearchParams` survive exactly one navigation.
 */
export function getForwardedAttributionParams(
  search: URLSearchParams = new URLSearchParams(window.location.search),
): Record<string, string> {
  return attributionParams(search);
}

/**
 * Copies the attribution params on the current URL onto `url`.
 *
 * The client-side half of the lift the backend does on the same redirect (see
 * AppendUnverifiedMemberSignupRedirectQueryParams). The backend can only lift
 * from a referer the request carried, so when a navigation dropped it, what
 * this page still holds is the last copy of the campaign.
 *
 * Anything `url` already carries wins — the backend resolved it for that hop
 * from cookies the browser cannot see, and `referer` is never copied for the
 * same reason. Relative URLs stay relative; the caller hands this to `navigate`.
 */
export function withSignupAttributionParams(
  url: string,
  search: URLSearchParams = new URLSearchParams(window.location.search),
): string {
  const params = getForwardedAttributionParams(search);
  if (Object.keys(params).length === 0) {
    return url;
  }

  let parsed: URL;
  let isRelative = false;
  try {
    parsed = new URL(url);
  } catch {
    try {
      parsed = new URL(url, window.location.origin);
      isRelative = true;
    } catch {
      return url;
    }
  }

  for (const [key, value] of Object.entries(params)) {
    if (!parsed.searchParams.has(key)) {
      parsed.searchParams.set(key, value);
    }
  }

  return isRelative ? `${parsed.pathname}${parsed.search}${parsed.hash}` : parsed.toString();
}
